import {
  MDBCol,
  MDBDropdown,
  MDBDropdownItem,
  MDBDropdownMenu,
  MDBDropdownToggle,
  MDBRow,
} from "mdbreact";
import { planText } from "../../core/constants/strings";
import Skeleton from "react-loading-skeleton";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../core/hooks/useAuth";
import {
  FLEX_BENEFITS_PRODUCT_TYPE,
  useFlexPackages,
} from "./FlexBenefitsPlanCard";
import { useFlexBenefitsIdCardFeatureFlag } from "../../core/hooks/useFlexBenefitsIdCardFeatureFlag";
import IdCardDownloadButton from "./IdCardDownloadButton";
import { Plan } from "../../domain/entity/Plan";

type DropdownPlan = Plan & { flexPackageName?: string };

// FLEX_BENEFITS entries aren't real Plan rows (FlexBenefits packages have no
// `application` row of their own -- see FlexBenefitsPlanCard.tsx), so this label
// bypasses planText's generic "{plan} plan" templating, which would otherwise show
// the raw enum ("flex_benefits plan") instead of the package's real name.
function planLabel(plan: DropdownPlan | null): string {
  if (!plan) return "";
  if (plan?.productType === FLEX_BENEFITS_PRODUCT_TYPE) {
    return plan.flexPackageName ?? planText.replace("{plan}", "FlexBenefits");
  }
  return planText.replace("{plan}", plan?.productType?.toLowerCase() ?? "");
}

const IdCardDropdown = () => {
  const auth = useAuth();
  const { packages: flexPackages } = useFlexPackages();
  const flexIdCard = useFlexBenefitsIdCardFeatureFlag();
  const [plan, setPlan] = useState<DropdownPlan | null>(null);
  const [activePlans, setActivePlans] = useState<DropdownPlan[]>([]);

  useEffect(() => {
    const rawPlans = auth.application?.plans ?? [];
    // Pull a subset of all plans that only includes active plans
    const onlyActive = rawPlans.filter((p) => p.active);
    // If there is a plan with productType `MEDICAL`, sort it to the front of the list of plans
    const medicalFirst = onlyActive.sort((a) => {
      if (a.productType === "MEDICAL") return -1;
      return 0;
    });

    // PPI-1391 Slice 4 placeholder, behind its own flag. A flex package has no
    // `application` row, so it borrows its bundled STM policy's appId
    // (enrollment.appPid) -- the same identity check every other plan here
    // already passes. Appended after medicalFirst so it never becomes the
    // default selection ahead of a member's real medical plan.
    const flexEntries: DropdownPlan[] = flexIdCard.enabled
      ? flexPackages
          .filter((pkg) => pkg.enrollment?.appPid !== undefined)
          .map(
            (pkg, index): DropdownPlan => ({
              ...new Plan(
                -(index + 1),
                pkg.enrollment?.appPid,
                FLEX_BENEFITS_PRODUCT_TYPE,
                false,
                true
              ),
              flexPackageName: pkg.name,
            })
          )
      : [];

    // Set the active plans as the sorted array of plans
    setActivePlans([...medicalFirst, ...flexEntries]);
  }, [auth.application, flexPackages, flexIdCard.enabled]);

  useEffect(() => {
    if (activePlans.length === 0) {
      return;
    }
    // Keep a manually-selected plan intact across a re-run of the effect above
    // (e.g. flexIdCard.enabled resolving after the initial render) unless that
    // selection no longer exists in the new list -- otherwise every async flag
    // resolution silently snaps the dropdown back to activePlans[0].
    setPlan((current) => {
      const stillActive =
        current && activePlans.some((it) => it.id === current.id);
      return stillActive ? current : activePlans[0];
    });
  }, [activePlans]);

  return (
    <>
      <MDBRow>
        <MDBCol>
          {!auth.gettingApplication ? (
            (activePlans.length || 0) > 1 && (
              <MDBDropdown className="mb-3">
                <MDBDropdownToggle
                  caret
                  color="white"
                  className="btn-block btn-outline-light-grey d-flex align-items-center text-capitalize"
                >
                  <span className="font-weight-bold black-text mr-auto text-capitalize">
                    {planLabel(plan)}
                  </span>
                </MDBDropdownToggle>
                <MDBDropdownMenu
                  color="primary"
                  className="dropdown-custom w-100"
                >
                  {activePlans.map((it) => (
                    <MDBDropdownItem
                      key={it.id}
                      onClick={() => setPlan(it)}
                      className={`text-capitalize${
                        it.id === plan?.id ? " active" : ""
                      }`}
                    >
                      {planLabel(it)}
                    </MDBDropdownItem>
                  ))}
                </MDBDropdownMenu>
              </MDBDropdown>
            )
          ) : (
            <Skeleton height="2.85rem" className="mb-3" />
          )}
        </MDBCol>
      </MDBRow>
      <IdCardDownloadButton plan={plan} />
    </>
  );
};

export default IdCardDropdown;
